import React, { useEffect } from "react";
import parkourImage from "../../assets/ParkourLogo.svg";
import css_self from "./css/NavBar.module.scss";
import "./css/NavBar.scss";
import {
    Tooltip, Avatar, Box, Button, IconButton, Menu, MenuItem,
    Popover, Typography, Link
} from "@mui/material";
import {
    Blue3, Grey2, Grey17, Grey6, White, Grey5, Grey4, Blue4
} from "../../theme/styles";
// MqttClient hook keeps the WSS connection alive while the user is logged
// in and tracks probe-tagged messages (kind === MQTT_PROBE_KIND, sent by
// cres-aws-t13/scripts/mqtt/test-mqtt-echo.py) so the NavBar can surface a
// green test-probe indicator.
import { useMqttClient } from "../../util/MqttClient";
import AssistantChat from "../Assistant/AssistantChat";
import NotificationBell from "../Notifications/NotificationBell";
import { useNavigate } from "react-router-dom";
import {ArticleOutlined, CodeOutlined, DatasetOutlined, HelpOutlineOutlined, Hive, InsightsOutlined, Language, LockResetOutlined, Logout, MedicalServicesOutlined, PermIdentity, Settings, SupervisorAccount, SwitchAccount as SwitchAccountIcon, Sync, SyncAlt, ThumbUp, VerifiedOutlined, WorkOutlined as WorkOutline} from "@mui/icons-material";
import useAppContext from "../../util/AppContext";
import EventBus from "../../util/EventBus";
import { buildPositionTree } from "./positionTree";
import PositionTreeGuides, { ROW_PADDING } from "./PositionTreeGuides";
import { SessionContext, hasProduct} from "../../util/SessionContext";
import { buildSsoHandoffUrl , handoffVisibility} from "../../util/ssoAuthenticate";
import css_buttons from "../Common/css/CCButton.module.scss";
import axios from "axios";
import { SwitchAccount } from "../Login2/SwitchAccount";
import Login2 from "../Login2/Login2";
import ChangePasswordDialog from "../Login2/ChangePasswordDialog";

// THE MQTT PROBE DOT IS A TEST INSTRUMENT, NOT A PRODUCT FEATURE (Ed, 2026-10-29: "Make the dot on the
// NavBar only visible in the environment is not prod. Do this in all applications."). It is drawn
// only when the build's environment is not prod; the probe itself still runs, so the data attributes
// the smoke checks read are unaffected where the dot is drawn.
const SHOW_PROBE_DOT = process.env.REACT_APP_CRES_ENV !== "prod";

type MenuSelection = { id: string; target: EventTarget & HTMLButtonElement };

export default function NavBar() {
    const navigate = useNavigate();

    const [showLoginDialog, setShowLoginDialog] = React.useState<boolean>(false);
    const [showSwitchAccount, setShowSwitchAccount] = React.useState<boolean>(false);
    const [showChangePassword, setShowChangePassword] = React.useState<boolean>(false);
    const [menu, setMenu] = React.useState<MenuSelection | null>(null);

    const sessionContext: SessionContext = useAppContext()?.sessionContext!;
    // useMqttClient subscribes to the global + per-user topics on connect
    // and tracks how many probe-tagged messages (kind === MQTT_PROBE_KIND,
    // sent by cres-aws-t13/scripts/mqtt/test-mqtt-echo.py) have been received.
    const { received: mqttReceived } = useMqttClient(sessionContext);

    // Brief visual ping when a PROBE message arrives. Production traffic
    // (notifications, sync events) does NOT trigger this — only payloads
    // carrying `kind: "cres-mqtt-echo-probe"` do.
    const [mqttPing, setMqttPing] = React.useState<boolean>(false);
    useEffect(() => {
        if (mqttReceived.probeCount === 0) return;
        setMqttPing(true);
        const id = setTimeout(() => setMqttPing(false), 700);
        return () => clearTimeout(id);
    }, [mqttReceived.probeCount]);

    useEffect(() => {
        const sessionUpdateListener = () => {
            setShowLoginDialog(!!sessionContext.getShowLoginDialog());
        };

        EventBus.session.addListener("update", sessionUpdateListener);
        EventBus.components.addListener("switch-account-close", onSwitchAccountClose);

        sessionUpdateListener();

        return () => {
            EventBus.session.removeListener("update", sessionUpdateListener);
            EventBus.components.removeListener("switch-account-close", onSwitchAccountClose);
        };
    }, [sessionContext]);

    function onSwitchAccountClose() {
        setShowSwitchAccount(false);
    }

    const content = (
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start" }}>
            <div style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                paddingLeft: 20,
                height: "50px",
                borderBottomWidth: "1px",
                borderBottomStyle: "solid",
                borderBottomColor: Grey2,
                backgroundColor: White
            }}>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start",
                              minWidth: 0 }}>
                    <div style={{ paddingTop: "0px", paddingRight: "2px", marginLeft: "-5px", cursor: "pointer" }} onClick={() => navigate("/")}>
                        <img className={css_self.ParkourLogo} src={parkourImage} alt="Home" />
                    </div>
                    <div style={{ paddingTop: "5px", marginLeft: "57px", marginRight: "4px" }}>
                        <Typography style={{ fontSize: 24, fontWeight: 400, color: Grey4 }}>|</Typography>
                    </div>
                    <Box sx={{ paddingTop: "7px", cursor: "pointer",
                              display: { xs: "none", sm: "block" } }} onClick={() => navigate("/")}>
                        <Typography style={{ fontSize: 24, fontWeight: 500, color: Blue4 }}>
                            &nbsp;&nbsp;ParkourAI
                        </Typography>
                    </Box>
                    <Box sx={{ paddingTop: "5px", marginLeft: "15px", marginRight: "4px",
                               display: { xs: "none", sm: "block" } }}>
                        <Typography style={{ fontSize: 24, fontWeight: 400, color: Grey4 }}>|</Typography>
                    </Box>
                    {/* IT ALWAYS NAVIGATED HOME; NOTHING SAID SO. Ed, from inside the editor:
                        "How do I get back to the Doc landing page?" The handler was already here --
                        what was missing was any sign that the word was a control, so it read as a
                        label. A title and a hover underline cost nothing and answer the question
                        without being asked. */}
                    <div style={{ paddingTop: "7px", cursor: "pointer" }} onClick={() => navigate("/")}
                         title="Home">
                        <Typography style={{ fontSize: 24, fontWeight: 400, color: Blue3 }}
                                    sx={{ "&:hover": { textDecoration: "underline" } }}>
                            &nbsp;&nbsp;eTMF
                        </Typography>
                    </div>
                </div>

                <div style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    flexGrow: 1,
                    minWidth: 0,
                    paddingTop: "15px",
                    marginLeft: "30px"
                }}>
                    {sessionContext.valid() ? (
                        <Typography component="div" style={{ fontSize: "15px", fontWeight: "400", color: Grey5 }}>
                            Welcome: <b>{sessionContext.getAccountName()}!</b>
                        </Typography>
                    ) : null}
                </div>

                <Box sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-end",
                    minWidth: { xs: 0, sm: "120px" },
                    paddingTop: "4px"
                }}>
                    {sessionContext.valid() && SHOW_PROBE_DOT && (() => {
                        // Probe-only indicator. Gray at rest; turns green on
                        // first probe receipt. The dot itself does not
                        // reflect connection status — connect churn would
                        // make it look noisy and obscure the test signal.
                        const everReceived = mqttReceived.probeCount > 0;
                        const dotColor = everReceived ? "#27AE60" : Grey5;
                        const tooltip = everReceived
                            ? `MQTT probes received: ${mqttReceived.probeCount}` +
                              (mqttReceived.lastTopic ? ` • last: ${mqttReceived.lastTopic}` : "")
                            : "Waiting for MQTT test probe…";
                        return (
                            <Tooltip title={tooltip}>
                                <Box
                                    sx={{
                                        width: 22, height: 22,
                                        marginTop: "10px", marginRight: "10px",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        position: "relative",
                                    }}
                                    aria-label="mqtt-status-indicator"
                                    data-cc-mqtt-probe-count={mqttReceived.probeCount}
                                    data-cc-mqtt-total-count={mqttReceived.totalCount}
                                >
                                    <Box
                                        sx={{
                                            width: 10, height: 10, borderRadius: "50%",
                                            backgroundColor: dotColor,
                                            transition: "transform 220ms ease, box-shadow 220ms ease",
                                            transform: mqttPing ? "scale(1.6)" : "scale(1)",
                                            boxShadow: mqttPing
                                                ? "0 0 0 6px rgba(91,141,239,0.35)"
                                                : "0 0 0 0 rgba(0,0,0,0)",
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        );
                    })()}
                    {/* THE BELL. Docs had none until mentions existed to put in it -- 'Docs' is a
                        value in cres_t2 notifications.APPLICATIONS now, which is what a frontend
                        fetches by. No MQTT push on this tier's hook yet, so it refetches when it
                        is opened: late, but never wrong. */}
                    {sessionContext.valid() && (
                        <div style={{ paddingTop: "1px", marginRight: "8px" }}>
                            <NotificationBell/>
                        </div>
                    )}
                    {sessionContext.valid() && (
                        <div style={{ width: "45px", paddingTop: "3px" }}>
                            <AssistantChat/>
                        </div>
                    )}
                    <div style={{ width: "45px", paddingTop: "3px" }}>
                        {renderSiteNavigation()}
                    </div>
                    {sessionContext.valid() && (
                        <div style={{ width: "45px", paddingTop: "3px" }}>
                            {renderVisibilityNavigation()}
                        </div>
                    )}
                    <div style={{ width: "45px", paddingTop: "1px" }}>
                        {renderAccountNavigation()}
                    </div>
                </Box>
            </div>
        </div>
    );

    return (
        <>
            {showSwitchAccount ? <SwitchAccount /> : null}
            {showChangePassword ? <ChangePasswordDialog onClose={() => setShowChangePassword(false)}/> : null}
            {content}
            {showLoginDialog ? <Login2 /> : null}
        </>
    );

    // ─────────────────────────────────────────────────────────────
    // Render Functions
    // ─────────────────────────────────────────────────────────────

    function goToExternalSite(baseUrl: string) {
        // Hand off this app's access token so the target can mint a native session.
        const accessToken = sessionContext.valid() ? sessionContext.getAccessToken() : null;
        setMenu(null);
        // THE ACTIVE ACCOUNT AND PERSONA ARE OFFERED, not imposed. The exchange verifies the
        // persona against the list it would itself have offered and refuses when it does not
        // belong, and the target falls back to asking -- so the hint can only save a question,
        // never answer one wrongly. The account half is safe outright: the suite is one database
        // partitioned by realm, so an account USID means the same thing on both sides.
        window.open(buildSsoHandoffUrl(baseUrl, accessToken, handoffVisibility(sessionContext)), "_blank", "noopener,noreferrer");
    }

    function renderSiteNavigation() {
        const siteCard = (icon: React.ReactNode, title: string, description: string, action: string | (() => void)) => {
            const go = typeof action === "string" ? () => navigate(action) : action;
            return (
                <>
                    <div style={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                        <IconButton
                            className={css_buttons.blueIconButton}
                            style={{ height: "72px", width: "72px" }}
                            onClick={() => go()}
                        >
                            {icon}
                        </IconButton>
                        <div style={{ marginLeft: "10px" }}>
                            <Link
                                underline="none"
                                variant="inherit"
                                color="inherit"
                                href="#"
                                onClick={(e) => { e.preventDefault(); go(); }}
                            >
                                <Typography style={{ fontSize: "22px", fontWeight: 500, color: "#000000", textAlign: "left" }}>
                                    {title}
                                </Typography>
                            </Link>
                        </div>
                    </div>
                    <div style={{ marginTop: "6px" }}>
                        <Typography style={{ fontSize: "13px", fontWeight: "400", color: Grey5, textAlign: "left" }}>
                            {description}
                        </Typography>
                    </div>
                </>
            );
        };

        const iconSx = { color: Blue3, width: "54px", height: "54px" };
        const apps = [
            ...(hasProduct("administration")
                ? [siteCard(<Settings sx={iconSx} />, "Administration", "Manage users, roles, tenants, and suite-wide configuration.", () => goToExternalSite(process.env.REACT_APP_ADMINISTRATION_URL || "https://admin.cres-intelligence.io"))]
                : []),
            // GATED ON `tmf` (2026-11-04), like every other tile. It was drawn for everybody and pointed at a route no app
            // serves; the server offers ParkourAI eTMF (t13) to nobody until it has privileges to gate on (products.py).
            ...(hasProduct("tmf")
                ? [siteCard(<MedicalServicesOutlined sx={iconSx} />, "Clinical Trials", "Plan, execute, and report on clinical studies — protocols, sites, participants, and outcomes.", () => goToExternalSite(process.env.REACT_APP_TMF_URL || "https://tmf.cres-intelligence.io"))]
                : []),
            ...(hasProduct("dataCatalog")
                ? [siteCard(<DatasetOutlined sx={iconSx} />, "Data Catalog", "Discover, classify, and govern data assets across your organization.", () => goToExternalSite(process.env.REACT_APP_DATA_CATALOG_URL || "https://dc.cres-intelligence.io"))]
                : []),
            ...(hasProduct("developer")
                ? [siteCard(<CodeOutlined sx={iconSx} />, "Developer", "API explorer, SDKs, and documentation for building integrations.", () => goToExternalSite(process.env.REACT_APP_DEVELOPER_URL || "https://developer.cres-intelligence.io"))]
                : []),
            ...(hasProduct("qms")
                ? [siteCard(<Hive sx={iconSx} />, "QMS", "Core platform services, identity, and shared building blocks for the suite.", () => goToExternalSite(process.env.REACT_APP_FOUNDATION_URL || "https://qms.cres-intelligence.io"))]
                : []),
            // GATED ON `docs`, like every other product tile: the launcher must not offer a door
            // the other end refuses. The server settles which products are open at login
            // (cres/utils/products.py) -- any privilege over documents opens Docs, which is how a
            // document-control clerk reaches it with what they already hold, and how an external
            // reviewer holding only `document:author` gets a launcher with Docs and nothing else.
            ...(hasProduct("docs")
                ? [siteCard(<ArticleOutlined sx={iconSx} />, "ParkourAI Documents", "Author and review the text of controlled documents \u2014 collaboratively, with suggestions and approvals.", () => goToExternalSite(process.env.REACT_APP_DOCS_URL || "https://docs.cres-intelligence.io"))]
                : []),
            // GATED, because the launcher must not offer a door the other end refuses. Every
            // Reporting service action requires `reporting`; without the grant this tile leads to
            // an app that loads and then 403s on everything it fetches. Same reasoning, and the
            // same shape, as the other gated tiles here.
            ...(hasProduct("reporting")
                ? [siteCard(<InsightsOutlined sx={iconSx} />, "Reporting", "Query your own records — including the answers on your forms — and build analytical views over them.", () => goToExternalSite(process.env.REACT_APP_REPORTING_URL || "https://reports.cres-intelligence.io"))]
                : []),
            // GATED ON `trace`, like every other product tile.
            ...(hasProduct("trace")
                ? [siteCard(<VerifiedOutlined sx={iconSx} />, "ParkourAI Trace", "Design controls for regulated software: requirements, risks, code and tests traced from Jira and GitHub, with a release gate.", () => goToExternalSite(process.env.REACT_APP_TRACE_URL || "https://trace.cres-intelligence.io"))]
                : []),
        ];

        return (
            <div>
                <Tooltip title="Sites Portfolio" placement="bottom">
                    <IconButton
                        className={css_self.NavMenuButton}
                        style={{ marginTop: "-1px" }}
                        onClick={(e) => setMenu({ id: "sites", target: e.currentTarget })}
                    >
                        <Language />
                    </IconButton>
                </Tooltip>

                {menu?.id === "sites" && (
                    <Popover
                        anchorEl={menu.target}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: -15, horizontal: "left" }}
                        open={Boolean(menu.target)}
                        onClose={() => setMenu(null)}
                        slotProps={{
                            paper: { elevation: 2 },
                        }}
                    >
                        <Box sx={{ p: 2, bgcolor: "background.paper" }}>
                            <div style={{ minWidth: "320px", maxHeight: "80vh", overflowY: "auto", display: "flex", flexDirection: "column" }}>
                                {apps.map((card, idx) => (
                                    <div key={idx} style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "flex-start",
                                        marginBottom: "10px",
                                        border: "1px solid #EDEFF3",
                                        borderRadius: "10px",
                                        padding: "15px",
                                        boxSizing: "border-box"
                                    }}>
                                        {card}
                                    </div>
                                ))}
                            </div>
                        </Box>
                    </Popover>
                )}
            </div>
        );
    }

    function stringAvatar(name: string) {
        return name
            .split(" ")
            .map(word => word[0])
            .join("")
            .substring(0, 2);
    }


    function renderVisibilityNavigation() {
        const account: any = sessionContext.getAccount();
        if (!account) return null;

        const positions = account.Positions ?? [];
        const userGroups = account.UserGroups ?? [];
        const workGroups = account.WorkGroups ?? [];
        if (!positions.length && !userGroups.length && !workGroups.length) return null;

        const choose = (mode: string, value: string) => {
            setMenu(null);
            sessionContext.setActiveVisibility(mode, value);
            // The screens listen for this rather than reading storage on every render: changing
            // persona changes WHICH RECORDS EXIST for this caller, so a list that did not refetch
            // would keep showing the last persona's documents under the new one's name.
            EventBus.visibility.emit("change-visibility", mode);
        };

        return (
            <>
                <Tooltip title="Personas" placement="bottom">
                    <IconButton aria-label="Personas" className={css_self.NavMenuButton}
                                style={{ marginTop: "-1px" }}
                                onClick={(e) => setMenu({ id: "visibility", target: e.currentTarget })}>
                        <SyncAlt />
                    </IconButton>
                </Tooltip>
                {menu?.id === "visibility" ? (
                    <Menu anchorEl={menu?.target}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: -17, horizontal: "right" }}
                          open={true}
                          onClose={() => setMenu(null)}
                          slotProps={{
                              list: { disablePadding: true, style: { minWidth: "300px" } },
                              transition: { onExited: () => setMenu(null) },
                          }}>
                        {positions.length > 0 ? (
                            <>
                                <MenuItem key="position-header" disabled className={css_self.Standard}>
                                    <div style={{ display: "block", marginLeft: "10px" }}>
                                        <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>Positions</Typography>
                                    </div>
                                </MenuItem>
                                {/* THE TREE, NOT A FLAT LIST. A position means little without the
                                    ones above it -- two tenants routinely have a "Manager" in
                                    different parts of the chart, and picking the wrong one is how
                                    somebody ends up looking at an empty screen and concluding the
                                    app is broken. PositionAncestors are display context only: they
                                    are rendered unselectable, because they are not personas this
                                    user holds. */}
                                {buildPositionTree(positions, account.PositionAncestors ?? []).map((row: any) => (
                                    <MenuItem key={row.node.USID} className={css_self.Standard}
                                              sx={{ alignItems: "stretch",
                                                    paddingTop: `${ROW_PADDING}px`,
                                                    paddingBottom: `${ROW_PADDING}px` }}
                                              disabled={!row.selectable}
                                              onClick={row.selectable ? () => choose("Position", row.node.USID) : undefined}>&emsp;&emsp;
                                        <PositionTreeGuides row={row} />
                                        <SupervisorAccount style={{ color: row.selectable ? Grey17 : Grey6, alignSelf: "center" }} />
                                        <div style={{ display: "flex", alignItems: "center", marginLeft: "10px" }}>
                                            <Typography style={{ fontSize: "15px", fontWeight: "400",
                                                                 color: row.selectable ? Grey17 : Grey6 }}>
                                                {row.node.Name}
                                            </Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </>
                        ) : null}
                        {userGroups.length > 0 ? (
                            <>
                                <MenuItem key="userGroup-header" disabled className={css_self.Standard}>
                                    <div style={{ display: "block", marginLeft: "10px" }}>
                                        <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>User Groups</Typography>
                                    </div>
                                </MenuItem>
                                {userGroups.map((userGroup: any) => (
                                    <MenuItem key={userGroup.USID} className={css_self.Standard}
                                              onClick={() => choose("User Group", userGroup.USID)}>&emsp;&emsp;
                                        <ThumbUp style={{ color: Grey17 }} />
                                        <div style={{ display: "block", marginLeft: "10px" }}>
                                            <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>{userGroup.Name}</Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </>
                        ) : null}
                        {workGroups.length > 0 ? (
                            <>
                                <MenuItem key="workgroup-header" disabled className={css_self.Standard}>
                                    <div style={{ display: "block", marginLeft: "10px" }}>
                                        <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>Work Groups</Typography>
                                    </div>
                                </MenuItem>
                                {workGroups.map((workgroup: any) => (
                                    <MenuItem key={workgroup.USID} className={css_self.Standard}
                                              onClick={() => choose("Work Group", workgroup.USID)}>&emsp;&emsp;
                                        <WorkOutline style={{ color: Grey17 }} />
                                        <div style={{ display: "block", marginLeft: "10px" }}>
                                            <Typography style={{ fontSize: "15px", color: Grey17, fontWeight: "400" }}>{workgroup.Name}</Typography>
                                        </div>
                                    </MenuItem>
                                ))}
                            </>
                        ) : null}
                    </Menu>
                ) : null}
            </>
        );
    }

    function renderAccountNavigation() {
        if (!sessionContext.valid()) {
            return (
                <IconButton
                    className={css_self.NavMenuButton}
                    onClick={() => {
                        sessionContext.setShowLoginDialog?.(true);
                        setShowLoginDialog(true);
                    }}
                >
                    <PermIdentity />
                </IconButton>
            );
        }

        return (
            <div>
                <Button
                    className={css_self.NavMenuButton}
                    variant="text"
                    onClick={(e) => setMenu({ id: "account", target: e.currentTarget })}
                >
                    <Avatar sx={{ marginTop: "2px", bgcolor: Blue3, width: 35, height: 35 }}>
                        {stringAvatar(sessionContext.getName())}
                    </Avatar>
                </Button>
                {menu?.id === "account" && (
                    <Menu
                        anchorEl={menu.target}
                        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                        transformOrigin={{ vertical: -16, horizontal: "center" }}
                        open={Boolean(menu.target)}
                        onClose={() => setMenu(null)}
                        slotProps={{
                            paper: { elevation: 2 },
                            list: {
                                disablePadding: true,
                                style: { minWidth: "200px" },
                            }
                        }}>
                        <MenuItem disabled>
                            <Typography component="div" style={{ fontSize: "15px", fontWeight: "400", color: Grey17 }}>
                                Account: <b>{sessionContext.getAccountName()}</b>
                            </Typography>
                        </MenuItem>
                        <MenuItem disabled>
                            <Typography component="div" style={{ fontSize: "15px", fontWeight: "400", color: Grey17 }}>
                                Hello, <b>{sessionContext.getName()}</b>
                            </Typography>
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); navigate("/help"); }}>
                            <HelpOutlineOutlined style={{ color: Grey6, marginRight: 8 }} />
                            Help
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); setShowChangePassword(true); }}>
                            <LockResetOutlined style={{ color: Grey6, marginRight: 8 }} />
                            Change password
                        </MenuItem>
                        <MenuItem divider onClick={() => { setShowSwitchAccount(true); setMenu(null); }}>
                            <SwitchAccountIcon style={{ color: Grey6, marginRight: 8 }} />
                            Switch Account
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); refresh(); }}>
                            <Sync style={{ color: Grey6, marginRight: 8 }} />
                            Refresh
                        </MenuItem>
                        <MenuItem divider onClick={() => { setMenu(null); navigate("/logout"); }}>
                            <Logout style={{ color: Grey6, marginRight: 8 }} />
                            Log out
                        </MenuItem>
                    </Menu>
                )}
            </div>
        );
    }

    async function refresh() {
        axios({
            method: "PUT",
            baseURL: process.env.REACT_APP_API_URL,
            url: "t13/v1/authenticate",
            data: {
                "X-Cres-RefreshToken": sessionContext.getRefreshToken(),
            },
        }).then((response) => {
            // Persist the refreshed session so administrative-data changes (new Positions / User
            // Groups / Work Groups, roles, privileges) appear WITHOUT a full logout/login. Previously
            // the response was discarded and the page just reloaded, re-reading stale sessionStorage.
            // Active persona (ActiveVisibility) is intentionally preserved. Mirrors Login2 storage.
            sessionContext.setIdentityToken(response.data["IdentityToken"]);
            sessionContext.setSessionUSID(response.data["Session"]);
            sessionContext.setAccessToken(response.data["AccessToken"]);
            sessionContext.setRefreshToken(response.data["RefreshToken"]);
            sessionContext.setAccount(response.data["Account"]);
            sessionContext.setAccountName(response.data["Account"]["AccountName"]);
            sessionContext.setAccountUserUSID(response.data["Account"]["AccountUserUSID"]);
            sessionContext.setRoles(response.data["Roles"]);
            sessionContext.setPrivileges(response.data["Privileges"]);
            sessionContext.setProducts(response.data["Products"] || []);
            sessionContext.setPrivilegeConstraints(response.data["PrivilegeConstraints"]);
            sessionContext.setTenants(response.data["Tenants"]);
            sessionContext.setPickValues(response.data["PickValues"]);
            window.location.href = "/";
        }).catch(() => {
            EventBus.error.emit("show-system-error");
        });
    }
}
